/**
 * Sales Data Updater for Real-time Sales Pressure Messages
 * Automatically updates product metafields with real sales data
 * 
 * Usage: Add this script to your theme to automatically update sales data
 * Or run manually via browser console or admin
 */

class SalesDataUpdater {
  constructor(options = {}) {
    this.apiVersion = '2024-01';
    this.period = options.period || 30; // days
    this.debug = options.debug || false;
  }

  /**
   * Fetch orders for a specific product from the last N days
   */
  async getProductSalesData(productId, days = 30) {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);

      const startDateISO = startDate.toISOString();
      const endDateISO = endDate.toISOString();

      // Note: This requires admin API access
      const response = await fetch(`/admin/api/${this.apiVersion}/orders.json?created_at_min=${startDateISO}&created_at_max=${endDateISO}&status=any&limit=250`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.getAccessToken()
        }
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      return this.calculateSalesForProduct(data.orders, productId);
    } catch (error) {
      console.error('Error fetching sales data:', error);
      return null;
    }
  }

  /**
   * Calculate total sales for a specific product from orders
   */
  calculateSalesForProduct(orders, productId) {
    let totalSales = 0;
    let salesDates = [];

    orders.forEach(order => {
      order.line_items.forEach(item => {
        if (item.product_id === productId) {
          totalSales += item.quantity;
          salesDates.push(new Date(order.created_at));
        }
      });
    });

    return {
      totalSales,
      salesDates,
      period: this.period
    };
  }

  /**
   * Update product metafields with sales data
   */
  async updateProductMetafields(productId, salesData) {
    try {
      const metafields = [
        {
          key: 'sales_count_30_days',
          value: salesData.totalSales.toString(),
          type: 'number_integer',
          namespace: 'custom'
        },
        {
          key: 'sales_data_updated',
          value: new Date().toISOString(),
          type: 'date_time',
          namespace: 'custom'
        }
      ];

      for (const metafield of metafields) {
        const response = await fetch(`/admin/api/${this.apiVersion}/products/${productId}/metafields.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Access-Token': this.getAccessToken()
          },
          body: JSON.stringify({ metafield })
        });

        if (!response.ok) {
          console.error(`Failed to update metafield ${metafield.key}:`, response.status);
        }
      }

      if (this.debug) {
        console.log(`✅ Updated sales data for product ${productId}:`, salesData);
      }

      return true;
    } catch (error) {
      console.error('Error updating metafields:', error);
      return false;
    }
  }

  /**
   * Process sales data for a single product
   */
  async processProduct(productId) {
    if (this.debug) {
      console.log(`🔄 Processing product ${productId}...`);
    }

    const salesData = await this.getProductSalesData(productId, this.period);
    
    if (salesData) {
      await this.updateProductMetafields(productId, salesData);
      return salesData;
    }

    return null;
  }

  /**
   * Process multiple products
   */
  async processProducts(productIds) {
    const results = [];
    
    for (const productId of productIds) {
      const result = await this.processProduct(productId);
      results.push({ productId, data: result });
      
      // Rate limiting - wait 500ms between requests
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    return results;
  }

  /**
   * Get access token (implement this based on your setup)
   */
  getAccessToken() {
    // This should be implemented based on your authentication method
    // For development, you might use a private app token
    // For production, use proper OAuth flow
    throw new Error('Access token method not implemented. Please configure authentication.');
  }

  /**
   * Manual update for testing (browser console)
   */
  static manualUpdate(productId, salesCount) {
    // This can be used for manual testing without API
    const metafields = {
      'custom.sales_count_30_days': salesCount,
      'custom.sales_data_updated': new Date().toISOString()
    };

    console.log('Manual sales data update:', metafields);
    console.log('Apply these metafields to product', productId);
    
    return metafields;
  }
}

// Usage examples:

// 1. Manual update for testing (run in browser console):
// SalesDataUpdater.manualUpdate(123456789, 45);

// 2. Automatic update (requires proper API setup):
// const updater = new SalesDataUpdater({ debug: true });
// updater.processProduct(123456789);

// 3. Batch update multiple products:
// updater.processProducts([123456789, 987654321]);

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SalesDataUpdater;
}

// Global access for browser console
window.SalesDataUpdater = SalesDataUpdater; 